import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {UntypedFormControl, Validators} from '@angular/forms';
import {MatLegacySnackBar as MatSnackBar, MatLegacySnackBarConfig as MatSnackBarConfig} from '@angular/material/legacy-snack-bar';
import {ClipboardService} from 'ngx-clipboard';
import {BehaviorSubject, Subscription} from 'rxjs';
import {take} from 'rxjs/operators';

import {
    MIN_PASSWORD_LENGTH,
    PASSWORD_STRENGTH_BAR_LABEL,
    PASSWORD__STRENGTH_COLORS,
    MIN_LENGTH_ERROR_MESSAGE,
    PASSWORD_MATCH_MESSAGE,
    PASSWORD_MATCH_ERROR_MESSAGE,
    PASSWORD_REQUIRED_MESSAGE,
    CONFIRM_PASSWORD_REQUIRED_MESSAGE,
    EQUAL_TO_EMAIL_ERROR_MESSAGE,
    EQUAL_TO_USERNAME_ERROR_MESSAGE,
    PASSWORD_ERROR_BODY_MESSAGE,
    PASSWORD_CONTAIN_FORBIDDEN_CHARACTERS_MESSAGE,
    DefaultPasswordErrorMessages,
    hasNumberValidator,
    hasUpperValidator,
    hasCharacterValidator,
    hasLowerValidator,
    passwordHasNotAllowableCharacterValidator,
    confirmPasswordValidator,
    COPY_MESSAGE
} from '../../utils/password-utils';
import {environment} from '@env/environment';
import {PasswordFactory} from '@app/views/admin/application-settings/models/password';

@Component({
    selector: 'ngc-password',
    templateUrl: './password.component.html',
    styleUrls: ['./password.component.scss']
})
export class PasswordComponent implements OnInit, OnDestroy {

    @Input() userEmail: string;
    @Input() passwordCtrl: UntypedFormControl;
    @Input() confirmPasswordCtrl: UntypedFormControl = null;
    @Input() label: string;
    @Input() isConfirmPassword: boolean;
    @Input() isCurrentPassword: boolean;
    @Input() isVertical: boolean;
    @Input() hasGenerateBtn = false;
    @Input() hidePassword$: BehaviorSubject<boolean>;
    @Input() hasPasswordErrors: boolean;

    @Input()
    set required(required: boolean) {

        if (this.passwordCtrl
            && (required !== this._required)
            && this.passwordCtrl.validator) {

            this.initPasswordControl(required);
            this.passwordCtrl.updateValueAndValidity();
        }
        this._required = required;
    }
    get required(): boolean {
        return this._required;
    }

    @Output() validateConfirmPassword = new EventEmitter<boolean>();

    public passwordErrorMessagesList = [];
    public countOfErrors: number;
    public subscriptions: Subscription[] = [];
    private _required: boolean;
    readonly MIN_PASSWORD_LENGTH = MIN_PASSWORD_LENGTH;
    readonly PASSWORD_STRENGTH_BAR_LABEL = PASSWORD_STRENGTH_BAR_LABEL;
    readonly PASSWORD__STRENGTH_COLORS = PASSWORD__STRENGTH_COLORS;
    readonly MIN_LENGTH_ERROR_MESSAGE = MIN_LENGTH_ERROR_MESSAGE;
    readonly PASSWORD_MATCH_MESSAGE = PASSWORD_MATCH_MESSAGE;
    readonly PASSWORD_MATCH_ERROR_MESSAGE = PASSWORD_MATCH_ERROR_MESSAGE;
    readonly PASSWORD_REQUIRED_MESSAGE = PASSWORD_REQUIRED_MESSAGE;
    readonly CONFIRM_PASSWORD_REQUIRED_MESSAGE = CONFIRM_PASSWORD_REQUIRED_MESSAGE;
    readonly EQUAL_TO_EMAIL_ERROR_MESSAGE = EQUAL_TO_EMAIL_ERROR_MESSAGE;
    readonly EQUAL_TO_USERNAME_ERROR_MESSAGE = EQUAL_TO_USERNAME_ERROR_MESSAGE;
    readonly PASSWORD_ERROR_BODY_MESSAGE = PASSWORD_ERROR_BODY_MESSAGE;
    readonly PASSWORD_CONTAIN_FORBIDDEN_CHARACTERS_MESSAGE = PASSWORD_CONTAIN_FORBIDDEN_CHARACTERS_MESSAGE;
    readonly DefaultPasswordErrorMessages = DefaultPasswordErrorMessages;
    readonly COPY_MESSAGE = COPY_MESSAGE;
    readonly IS_PRODUCTION = environment.production;

    constructor(public snackBar: MatSnackBar,
                private _clipboardService: ClipboardService) { }

    ngOnInit() {
        this.initPasswordControl(this.required);

        this.subscriptions.push(
            this.passwordCtrl.valueChanges
                .subscribe((passwordValue: string) => {
                    this.passwordErrorMessagesList = [];
                    if (passwordValue
                        && passwordValue.length >= MIN_PASSWORD_LENGTH) {

                        this.countErrors();
                    } else {
                        this.countOfErrors = 1;
                    }

                    if (!this.isConfirmPassword && this.confirmPasswordCtrl) {
                        this.confirmPasswordCtrl.updateValueAndValidity();
                    }
                }));
    }

    private initPasswordControl(required: boolean): void {
        const passwordValidators = [];

        if (required) {
            passwordValidators.push(Validators.required);
        }

        if (!this.isCurrentPassword) {
            if (!this.isConfirmPassword) {
                passwordValidators.push(
                    Validators.minLength(this.MIN_PASSWORD_LENGTH),
                    Validators.maxLength(512),
                    passwordHasNotAllowableCharacterValidator
                );

                if (this.IS_PRODUCTION) {
                    passwordValidators.push(
                        hasNumberValidator,
                        hasUpperValidator,
                        hasLowerValidator,
                        hasCharacterValidator,
                        this.isNotEqualToEmailValidator.bind(this)
                    );
                }
            } else {
                passwordValidators.push(confirmPasswordValidator);
            }
        }

        this.passwordCtrl.setValidators(passwordValidators);
        this.passwordCtrl.updateValueAndValidity();
    }

    private countErrors() {
        this.countOfErrors = 0;
        for (const error in this.passwordCtrl.errors) {
            if (error && this.DefaultPasswordErrorMessages[error]) {
                this.passwordErrorMessagesList.push(this.DefaultPasswordErrorMessages[error]);
            } else {
                this.countOfErrors++;
            }
        }

        if (this.passwordErrorMessagesList.length > 0) {
            this.countOfErrors++;
        }

        // for a case when error is written on two rows
        if (this.passwordErrorMessagesList.length > 1
            && (this.passwordErrorMessagesList.indexOf(this.DefaultPasswordErrorMessages.hasCharacterError) !== -1)) {

            this.countOfErrors++;
        }
    }

    public onGenerateAndCopyPassword(): void {
        const generatedPassword = this.generatePassword();

        this.setFormControlValue(this.passwordCtrl, generatedPassword);
        if (this.confirmPasswordCtrl) {
            this.setFormControlValue(this.confirmPasswordCtrl, generatedPassword);
        }

        this.copyPassword(generatedPassword);
    }

    private setFormControlValue(formControl: UntypedFormControl, generatedPassword: string) {
        formControl.setValue(generatedPassword);
        formControl.markAsDirty();
        formControl.updateValueAndValidity();
        formControl.markAsTouched();
    }

    private generatePassword(): string {
        // password length 14-16 charachters
        const passwordLength = this.getRandomInt(this.MIN_PASSWORD_LENGTH + 4, this.MIN_PASSWORD_LENGTH + 6);
        return PasswordFactory.generatePassword(passwordLength);
    }

    private getRandomInt(min: number, max: number): number {
        min = Math.ceil(min);
        max = Math.floor(max);
        return Math.floor(Math.random() * (max - min + 1)) + min;
    }

    private copyPassword(password: string): void {
        this._clipboardService.copyFromContent(password);
        this.snackBar.open(this.COPY_MESSAGE, '', <MatSnackBarConfig>{duration: 3000});
    }

    private isNotEqualToEmailValidator(control: UntypedFormControl): { [s: string]: boolean } {
        if (control.value && this.userEmail) {
            if (control.value.toLowerCase() === this.userEmail.toLowerCase()) {
                return {'isNotEqualToEmailError': true};
            }
        }
        return null;
    }

    public onToggleHideShowPassword(): void {
        this.subscriptions.push(
            this.hidePassword$.pipe(take(1))
                .subscribe((hidePassword: boolean) => {
                    this.hidePassword$.next(!hidePassword);
                }));
    }

    public ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }
}
